import { useCallback, useRef } from "react";

import { Form } from "antd";
import { EditorState, LexicalEditor } from "lexical";
import { ValidateErrorEntity } from "rc-field-form/lib/interface";
import { useNavigate, useParams } from "react-router-dom";

import { BADGE_STATUS, RVFormPageLayout, lexicalToHtml, lexicalToText } from "@regal-voice/ui";
import { ADSettingsPaths } from "Pages/settings/Pages/AgentDesktop/paths";
import { FormSkeleton } from "Pages/settings/Pages/UserManagement/Components/FormSkeleton/FormSkeleton";
import { SETTINGS_NAV_TITLES_PATCH } from "Pages/settings/settingsMetadata";
import {
    CommunicationTemplate,
    CommunicationTemplateChannel,
    CommunicationTemplateDTO,
    CommunicationTemplateState,
} from "Types/CommunicationTemplate";
import { ToggleCollapsiblePanelButton, useKnownPanels } from "Utils/CollapsiblePanels";

import CommunicationTemplateForm from "./CommunicationTemplateForm/CommunicationTemplateForm";
import CommunicationTemplateFormFooter from "./CommunicationTemplateForm/CommunicationTemplateFormFooter";
import { useCommunicationTemplateMutations } from "./Hooks/useCommunicationTemplateMutations";
import { useCommunicationTemplateQueries } from "./Hooks/useCommunicationTemplateQueries";

const defaultFormValues: Partial<CommunicationTemplateDTO> = {
    state: CommunicationTemplateState.UNSET,
    channel: CommunicationTemplateChannel.sms,
};

function getCommunicationTemplateTitle(templateId: string | undefined, templateName: string | undefined): string {
    if (!templateId) {
        return SETTINGS_NAV_TITLES_PATCH[`${ADSettingsPaths.templates}/new`];
    }

    return `Edit ${templateName ? `'${templateName}'` : "template"}`;
}

export default function CommunicationTemplatePage() {
    const { settings } = useKnownPanels();

    const navigate = useNavigate();
    const { templateId } = useParams<{ templateId: string }>();
    const editorRef = useRef<LexicalEditor>(null);
    const { isLoading, templateData } = useCommunicationTemplateQueries({
        templateId,
    });

    const title = getCommunicationTemplateTitle(templateId, templateData?.name);
    const state = templateData && {
        label: templateData.state,
        status: templateData.state === CommunicationTemplateState.LIVE ? BADGE_STATUS.ACTIVE : BADGE_STATUS.PENDING,
    };
    const friendlyId = templateId ? (templateData?.friendlyId ?? "") : "new";

    const [form] = Form.useForm();
    const { isEditMode, isMutatingData, createCommunicationTemplate, updateCommunicationTemplate } =
        useCommunicationTemplateMutations({ id: templateId });

    const isDraft = templateData?.state === CommunicationTemplateState.DRAFT || true;

    const formHasErrors = useCallback(async () => {
        try {
            await form.validateFields();
            return false;
        } catch (error) {
            if (error && typeof error === "object" && "errorFields" in error) {
                const formErrors = error as ValidateErrorEntity<CommunicationTemplate>;
                return Boolean(formErrors.errorFields.some(({ errors }) => errors.length));
            }

            return false;
        }
    }, [form]);

    const saveAsLive = useCallback(async () => {
        form.setFieldValue("state", CommunicationTemplateState.LIVE);
        const hasErrors = await formHasErrors();
        if (!hasErrors) {
            form.submit();
        }
    }, [form, formHasErrors]);

    const saveAsDraft = useCallback(async () => {
        form.setFieldValue("state", CommunicationTemplateState.DRAFT);
        const hasErrors = await formHasErrors();
        if (!hasErrors) {
            form.submit();
        }
    }, [form, formHasErrors]);

    const onFinish = useCallback(
        async (formData: CommunicationTemplateDTO) => {
            let serializedData = formData;

            const { bodySource, ...data } = formData;
            // determine which method to use for serializing data
            // for sms we want plain text
            // for email we want html (rich text)
            const serializeFn = data.channel === "sms" ? lexicalToText : lexicalToHtml;
            const preview = serializeFn(editorRef.current as LexicalEditor);
            serializedData = {
                ...data,
                body: preview,
                bodySource: JSON.stringify((bodySource as EditorState).toJSON()),
            };

            const mutation = isEditMode ? updateCommunicationTemplate : createCommunicationTemplate;
            const result = await mutation(serializedData, true);

            if (result?.createCommunicationTemplate) {
                navigate(`../${result.createCommunicationTemplate.id}`, { relative: "path" });
            }
        },
        [isEditMode, createCommunicationTemplate, updateCommunicationTemplate, navigate]
    );

    const footer = (
        <CommunicationTemplateFormFooter
            saveAsDraft={saveAsDraft}
            saveAsLive={saveAsLive}
            isUpdating={isMutatingData}
            isLoading={isLoading}
            isDraft={isDraft}
        />
    );

    const content = isLoading ? (
        <FormSkeleton fieldsCount={5} />
    ) : (
        <CommunicationTemplateForm
            form={form}
            initialFormValues={templateData ?? defaultFormValues}
            onFinish={onFinish}
            editorRef={editorRef}
        />
    );

    return (
        <RVFormPageLayout
            showStatus={!isLoading && Boolean(templateId?.length) && templateId !== "new"}
            id={friendlyId}
            title={title}
            state={state}
            wide
            prefix={<ToggleCollapsiblePanelButton panelName={settings.name} direction="left" />}
            backTo=".."
            backText="Back to Communication Templates"
            footer={footer}
        >
            {content}
        </RVFormPageLayout>
    );
}
